import { inject, Injectable } from '@angular/core';
import { EAnalyticsStatus, EMatchingType } from '@enums/campaign-analytics.enum';
import { EPlatform, EPlatformFeature } from '@enums/campaign.enum';
import { ICtaAnalyticsDeliverables, ICtaResultApi } from '@interfaces/campaign-analytics-api.interface';
import { IAnalyticsNode, ICtaKpiStatusable, ICtaResult } from '@interfaces/campaign-analytics.interface';
import { TranslateService } from '@ngx-translate/core';
import { CampaignDetailsUndesignatedExecutionOrderService } from '@pages/campaigns/campaign-details/campaign-details-undesignated-execution-order.service';
import { CampaignAnalyticsUtilitiesService } from '@utilities/campaign-analytics-utilities.service';
import { DeliverableUtilitiesService } from '@utilities/deliverable-utilities.service';
import { SocialUtilitiesService } from '@utilities/social-utilities.service';
import { maxBy } from 'lodash';
import { SocialContentTilePlatformService } from './social-content-tile-platform.service';
import {
  IPlatformFeatureLineClampValues,
  ITileConfiguration,
  ITileExpectedKpi,
  TTileContentStatus,
} from './social-content-tile.interface';

@Injectable({
  providedIn: 'root',
})
export class SocialContentTileService {
  private readonly socialContentTilePlatformService = inject(SocialContentTilePlatformService);
  private readonly campaignAnalyticsUtilitiesService = inject(CampaignAnalyticsUtilitiesService);
  private readonly deliverableUtilities = inject(DeliverableUtilitiesService);
  private readonly undesignatedExecutionOrderService = inject(CampaignDetailsUndesignatedExecutionOrderService);
  private readonly translate = inject(TranslateService);

  public getPlatformFeatureLineClampValues(
    platformFeature: EPlatformFeature,
    hasMedia = false,
  ): IPlatformFeatureLineClampValues {
    switch (platformFeature) {
      case EPlatformFeature.twitchStream:
      case EPlatformFeature.youtubeStream:
        return {
          description: 10,
          title: 10,
        };
      case EPlatformFeature.instagramPost:
      case EPlatformFeature.instagramReel:
      case EPlatformFeature.instagramStory:
        return {
          description: 5,
          title: 1,
        };
      case EPlatformFeature.youtubeVod:
      case EPlatformFeature.youtubeShort:
      case EPlatformFeature.tiktokPost:
        return {
          description: 3,
          title: 2,
        };
      case EPlatformFeature.facebookVideo:
        return {
          description: 5,
          title: 2,
        };
      case EPlatformFeature.facebookPost:
      case EPlatformFeature.xPost:
        return {
          description: hasMedia ? 5 : 20,
          title: 5,
        };
      default:
        return {
          description: 3,
          title: 2,
        };
    }
  }

  public getPlatformFeatureTileColor(platformFeature: EPlatformFeature, opacity?: number): string {
    const platform = SocialUtilitiesService.getPlatform(platformFeature);
    let baseColor = '';

    switch (platform) {
      case EPlatform.facebook:
        baseColor = 'var(--shikenso-social-facebook)';
        break;
      case EPlatform.youtube:
        baseColor = 'var(--shikenso-social-youtube)';
        break;
      case EPlatform.instagram:
        baseColor = 'var(--shikenso-social-instagram)';
        break;
      case EPlatform.tiktok:
        baseColor = 'var(--shikenso-social-tiktok-alt)';
        break;
      case EPlatform.x:
        baseColor = 'var(--shikenso-social-x)';
        break;
      case EPlatform.twitch:
        baseColor = 'var(--shikenso-social-twitch)';
        break;
      default:
        baseColor = 'transparent';
        break;
    }

    if (opacity !== undefined && opacity >= 0 && opacity <= 1) {
      return `color-mix(in srgb, ${baseColor} ${opacity * 100}%, transparent)`;
    }

    return baseColor;
  }

  public getPlatformFeatureSocialColor(platformFeature: EPlatformFeature): string {
    const platform = SocialUtilitiesService.getPlatform(platformFeature);

    switch (platform) {
      case EPlatform.facebook:
        return 'var(--shikenso-social-facebook)';
      case EPlatform.youtube:
        return 'var(--shikenso-social-youtube)';
      case EPlatform.instagram:
        return 'var(--shikenso-social-instagram)';
      case EPlatform.tiktok:
        return 'var(--shikenso-purple-base)';
      case EPlatform.x:
        return 'var(--shikenso-purple-base)';
      case EPlatform.twitch:
        return 'var(--shikenso-social-twitch)';
      default:
        return 'var(--shikenso-purple-base)';
    }
  }

  public getDefaultSelectedCtaId(ctas: ICtaResultApi[]): string | null {
    if (!ctas.length) {
      return null;
    }

    const bonus: ICtaResultApi[] = ctas.filter((c) => c.matchingType === EMatchingType.bonus);
    const matched: ICtaResultApi[] = ctas.filter((c) => c.matchingType === EMatchingType.matched);

    let chosen: ICtaResultApi | undefined;

    if (matched.length) {
      chosen = this.pickMaxScoreCtaResult(matched);
    } else if (bonus.length) {
      chosen = this.pickMaxScoreCtaResult(bonus);
    } else {
      chosen = this.pickMaxScoreCtaResult(ctas);
    }

    return chosen?.ctaId ?? null;
  }

  public prepareTileData(analyticsNode: IAnalyticsNode, ctaResult?: ICtaResult | null): ITileConfiguration {
    const ctaKpiStatusable: ICtaKpiStatusable = !ctaResult
      ? this.buildContentLevelCtaKpiStatusable(analyticsNode)
      : {
          color: this.campaignAnalyticsUtilitiesService.getStatusableDisplayConfig(ctaResult.status).color,
          ctaKpiStatus: {
            ...ctaResult.ctaKpiStatus,
            deliverableColor: this.campaignAnalyticsUtilitiesService.getStatusableDisplayConfig(ctaResult.status).color,
            kpiColor: this.campaignAnalyticsUtilitiesService.getStatusableDisplayConfig(ctaResult.statusKpi).color,
          },
          status: ctaResult.status,
          statusKpi: ctaResult.statusKpi,
        };

    const executionStatus = ctaResult?.matchingType ?? analyticsNode.executionStatus;

    const ctasList: ICtaResultApi[] = Array.from(analyticsNode.content?.ctas?.values() ?? []);
    const resolvedSelectedCtaId: string | null = ctaResult?.ctaId ?? this.getDefaultSelectedCtaId(ctasList);

    const matchingType = this.resolveMatchingType(analyticsNode, ctasList, resolvedSelectedCtaId);
    const executionType =
      analyticsNode.plannedExecution?.executionType ?? ctaResult?.plannedExecution?.executionType ?? null;
    const undesignatedExecutionOrder = this.resolveUndesignatedExecutionOrder(analyticsNode, resolvedSelectedCtaId);
    const contentStatus = this.resolveContentStatus(
      matchingType,
      analyticsNode,
      ctasList,
      resolvedSelectedCtaId,
      !ctaResult,
    );
    const deliverableTypeNames = this.resolveDeliverableTypeNames(analyticsNode, ctasList, resolvedSelectedCtaId);
    const expectedKpis = this.resolveExpectedKpis(analyticsNode);
    const observationCount = this.computeObservationCount(ctasList);

    const tileConfiguration: ITileConfiguration = {
      ...this.socialContentTilePlatformService.getPlatformFeatureSpecificData(analyticsNode),
      content: analyticsNode.content,
      contentStatus,
      creator: analyticsNode.creator,
      ctaKpiStatusable,
      deliverableTypeNames,
      executionDateTime: analyticsNode.executionTime,
      executionStatus,
      executionType,
      expectedKpis,
      id: analyticsNode.id,
      isDateOnlyExecution: analyticsNode.isDateOnlyExecution,
      isLive: analyticsNode.isLive,
      matchingType,
      nonExecutedCtaId: analyticsNode.nonExecutedCtaId,
      nonExecutedCtaName: analyticsNode.nonExecutedCtaName,
      observationCount,
      platformFeature: analyticsNode.platformFeature,
      selectedCtaId: resolvedSelectedCtaId,
      undesignatedExecutionOrder,
    };

    return tileConfiguration;
  }

  public pickMaxScoreCtaResult(list: ICtaResultApi[]): ICtaResultApi | undefined {
    return maxBy(list, (c) => c.ctaAnalytics?.matchingScore ?? Number.NEGATIVE_INFINITY);
  }

  private resolveMatchingType(
    analyticsNode: IAnalyticsNode,
    ctasList: ICtaResultApi[],
    selectedCtaId: string | null,
  ): EMatchingType {
    if (selectedCtaId) {
      const selectedCta = ctasList.find((cta) => cta.ctaId === selectedCtaId);

      if (selectedCta) {
        return selectedCta.matchingType;
      }
    }

    return analyticsNode.executionStatus;
  }

  private resolveUndesignatedExecutionOrder(
    analyticsNode: IAnalyticsNode,
    selectedCtaId: string | null,
  ): number | null {
    const resolvedPlannedExecution = analyticsNode.plannedExecution ?? null;

    if (!resolvedPlannedExecution) {
      return null;
    }

    const lookupKey = this.undesignatedExecutionOrderService.getLookupKey(
      analyticsNode.id,
      selectedCtaId ?? analyticsNode.nonExecutedCtaId,
      resolvedPlannedExecution,
    );

    if (!lookupKey) {
      return null;
    }

    return analyticsNode.undesignatedExecutionOrdersByLookupKey?.[lookupKey] ?? null;
  }

  private buildContentLevelCtaKpiStatusable(analyticsNode: IAnalyticsNode): ICtaKpiStatusable {
    const { content } = analyticsNode;

    if (!content) {
      return analyticsNode.mergedCtaResult;
    }

    const contentStatus = content.status;
    const contentStatusKpi = content.statusKpi;

    return {
      ...analyticsNode.mergedCtaResult,
      color: this.campaignAnalyticsUtilitiesService.getStatusableDisplayConfig(contentStatus).color,
      ctaKpiStatus: {
        ...analyticsNode.mergedCtaResult.ctaKpiStatus,
        deliverableColor: this.campaignAnalyticsUtilitiesService.getStatusableDisplayConfig(contentStatus).color,
        kpiColor: this.campaignAnalyticsUtilitiesService.getStatusableDisplayConfig(contentStatusKpi).color,
      },
      status: contentStatus,
      statusKpi: contentStatusKpi,
    };
  }

  private resolveContentStatus(
    matchingType: EMatchingType,
    analyticsNode: IAnalyticsNode,
    ctasList: ICtaResultApi[],
    selectedCtaId: string | null,
    useContentStatusDirectly = false,
  ): TTileContentStatus | null {
    if (matchingType !== EMatchingType.matched) {
      return null;
    }

    let status: EAnalyticsStatus | undefined;

    if (!useContentStatusDirectly && selectedCtaId) {
      const selectedCta = ctasList.find((cta) => cta.ctaId === selectedCtaId);
      status = selectedCta?.status;
    }

    if (!status) {
      status = analyticsNode.content?.status;
    }

    switch (status) {
      case EAnalyticsStatus.ok:
        return 'fulfilled';
      case EAnalyticsStatus.problem:
        return 'partiallyFulfilled';
      case EAnalyticsStatus.failed:
        return 'notFulfilled';
      default:
        return null;
    }
  }

  private resolveDeliverableTypeNames(
    analyticsNode: IAnalyticsNode,
    ctasList: ICtaResultApi[],
    selectedCtaId: string | null,
  ): string[] {
    let ctaAnalytics: ICtaAnalyticsDeliverables | null = null;

    if (selectedCtaId) {
      const selectedCta = ctasList.find((cta) => cta.ctaId === selectedCtaId);
      ctaAnalytics = selectedCta?.ctaAnalytics ?? null;
    }

    if (!ctaAnalytics) {
      ctaAnalytics = analyticsNode.emptyCtaAnalytics ?? null;
    }

    if (!ctaAnalytics) {
      return [];
    }

    const configs = this.deliverableUtilities
      .getDeliverableCollectionConfigs(ctaAnalytics)
      .filter((config) => !this.deliverableUtilities.isTrackingDeliverable(config.type));

    return configs
      .reduce<string[]>((names, config) => {
        const collection = ctaAnalytics[config.collectionKey];

        if (Array.isArray(collection)) {
          for (const item of collection) {
            names.push(item.name);
          }
        }

        return names;
      }, [])
      .sort((a, b) => a.localeCompare(b));
  }

  private resolveExpectedKpis(analyticsNode: IAnalyticsNode): ITileExpectedKpi[] {
    const expectedKpis = analyticsNode.plannedExecution?.expectedKpis;

    if (!expectedKpis?.length) {
      return [];
    }

    return expectedKpis.map((kpi) => ({
      name: this.translate.instant(`shared.expectedKpis.${kpi.kpi}`),
      value: kpi.expectedValue,
    }));
  }

  private computeObservationCount(ctasList: ICtaResultApi[]): number {
    let count = 0;

    for (const cta of ctasList) {
      if (!cta.ctaAnalytics) {
        continue;
      }

      for (const config of this.deliverableUtilities.getDeliverableCollectionConfigs(cta.ctaAnalytics)) {
        if (!this.deliverableUtilities.isTrackingDeliverable(config.type)) {
          continue;
        }

        const entries = (cta.ctaAnalytics[config.collectionKey] ?? []) as Array<{
          keywords?: Array<{ findingsCount: number }>;
        }>;

        for (const entry of entries) {
          if ((entry.keywords ?? []).some((k) => k.findingsCount > 0)) {
            count++;
          }
        }
      }
    }

    return count;
  }
}
